<template>
  <div class="licenseAccountApply" v-loading="loading">
    <!-- 状态横幅 -->
    <div
      v-if="status === STATUS.PENDING"
      class="statusBanner statusBanner--pending"
    >
      您的License申请正在审核中，请耐心等待。
    </div>
    <div
      v-else-if="status === STATUS.REJECTED"
      class="statusBanner statusBanner--rejected"
    >
      您的License申请审核未通过。原因：{{ rejectReason || "—" }}
    </div>
    <div
      v-else-if="status === STATUS.SUCCESS"
      class="statusBanner statusBanner--success"
    >
      恭喜，您的License申请已审核成功。
    </div>

    <!-- 成功后的文件包下载展示区域 -->
    <div v-if="status === STATUS.SUCCESS" class="licenseSuccessContainer">
      <div class="licenseFileSection">
        <h3 class="licenseFileTitle">License文件包</h3>
        <div class="licenseFileCard">
          <div class="licenseFileIconWrap">
            <i class="el-icon-folder-opened"></i>
          </div>
          <div class="licenseFileInfo">
            <div class="licenseFileName">
              {{ zipName || "License文件资料包.zip" }}
            </div>
            <div class="licenseFileSize">
              {{ zipSizeText ? zipSizeText : "" }} &middot;
              包含License授权文件及部署说明
            </div>
          </div>
          <el-button
            type="primary"
            icon="el-icon-download"
            circle
            class="licenseDownloadBtn"
            @click="handleDownloadZip"
          ></el-button>
        </div>
      </div>

      <!-- 提示小卡片 -->
      <div class="licenseTipCard">
        <i class="el-icon-warning-outline licenseTipIcon"></i>
        <span class="licenseTipText">
          请及时下载并妥善保管License文件，部署时需使用授权文件完成激活。如需帮助，请联系技术支持。
        </span>
      </div>
    </div>

    <template v-else>
      <h2 class="licenseAccountApplyTitle">
        PX配电专家软件License(含调试设备)申请
      </h2>
      <el-form
        ref="licenseForm"
        :model="form"
        :rules="rules"
        label-width="140px"
        :disabled="formDisabled"
        class="licenseAccountApplyForm"
      >
        <div class="licenseAccountApplyFields">
          <el-form-item label="审批人" prop="approver">
            <el-select
              v-model="form.approver"
              placeholder="请输入名称搜索并选择审批人"
              class="licenseAccountApplyFull"
              filterable
              remote
              :remote-method="searchApproverMethod"
              :loading="approverSearchLoading"
              @change="handleApproverChange"
            >
              <el-option
                v-for="item in approverOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="所在大区" prop="region">
            <el-select
              v-model="form.region"
              placeholder="请选择所在大区"
              class="licenseAccountApplyFull"
              disabled
            >
              <el-option
                v-for="item in regionOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="项目负责人" prop="projectLeader">
            <el-input
              v-model="form.projectLeader"
              placeholder="请输入"
              maxlength="50"
            />
          </el-form-item>
          <el-form-item label="负责人联系方式" prop="leaderPhone">
            <el-input
              v-model="form.leaderPhone"
              placeholder="请输入"
              maxlength="20"
            />
          </el-form-item>
          <el-form-item label="盘厂名称" prop="factoryName">
            <el-input
              v-model="form.factoryName"
              placeholder="请输入"
              maxlength="100"
            />
          </el-form-item>
          <el-form-item label="产品名称" prop="productName">
            <el-input
              v-model="form.productName"
              placeholder="请输入产品名称"
              maxlength="100"
            />
          </el-form-item>
          <el-form-item label="产品版本" prop="productVersion">
            <el-select
              v-model="form.productVersion"
              placeholder="请选择产品版本"
              class="licenseAccountApplyFull"
            >
              <el-option
                v-for="item in productVersionOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="计划部署时间" prop="plannedDeployTime">
            <el-date-picker
              v-model="form.plannedDeployTime"
              type="date"
              value-format="yyyy-MM-dd"
              placeholder="年/月/日"
              class="licenseAccountApplyFull"
            />
          </el-form-item>
          <el-form-item label="项目名称" prop="projectName">
            <el-input
              v-model="form.projectName"
              placeholder="请输入项目名称"
              maxlength="100"
            />
          </el-form-item>
          <el-form-item label="项目地址" prop="projectAddress">
            <el-input
              v-if="formDisabled"
              v-model="form.projectAddress"
              disabled
            />
            <el-cascader
              v-else
              ref="projectAddressCascader"
              v-model="projectAddressCodes"
              :class="[
                'licenseAccountApplyFull',
                'licenseAccountApplyCascader',
                { 'has-placeholder-value': form.projectAddress },
              ]"
              :props="areaCascaderProps"
              :disabled="mapDataLoading"
              :placeholder="form.projectAddress || '省/市/区/街道'"
              popper-class="licenseAccountApplyPopper"
              :append-to-body="true"
              clearable
              @change="handleProjectAddressChange"
            />
          </el-form-item>
          <el-form-item label="MAC地址" prop="macAddress">
            <el-input
              v-model="form.macAddress"
              placeholder="请输入MAC地址"
              maxlength="50"
            />
          </el-form-item>
          <el-form-item label="详细地址" prop="detailAddress">
            <el-input
              v-model="form.detailAddress"
              placeholder="详细地址"
              maxlength="200"
            />
          </el-form-item>
          <el-form-item
            label="调试设备"
            prop="debugDevice"
            class="licenseAccountApplyDebugItem"
          >
            <el-select
              v-model="form.debugDevice"
              placeholder="请选择"
              class="licenseAccountApplyFull"
            >
              <el-option
                v-for="item in debugDeviceOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
            <p class="licenseAccountApplyHint">盘厂首个落地项目可申请</p>
          </el-form-item>
          <el-form-item label="附件" prop="attachment">
            <FormFileUpload
              :url="form.attachment"
              :limit="1"
              :max-size="5"
              accept=".pdf,application/pdf"
              tip="上传文件格式仅支持pdf，且文件大小不得超过5M"
              :disabled="formDisabled"
              @upload="handleAttachmentUpload"
            />
          </el-form-item>
        </div>

        <div class="licenseAccountApplyFooter" v-if="!formDisabled">
          <el-button
            type="primary"
            class="licenseAccountApplySubmitBtn"
            :loading="submitting"
            @click="handleSubmit"
          >
            提交申请
          </el-button>
        </div>
      </el-form>
    </template>
  </div>
</template>

<script>
import FormFileUpload from "@/components/formFileUpload.vue";
import { fetchMapData } from "@/utils/mapData";
import {
  addLicenseApply,
  getLicenseApplyDetail,
  updateLicenseApply,
  getAuditorRegionList,
  searchAuditor,
} from "@/api/lx";
const PRODUCT_VERSION_OPTIONS = [
  { label: "PX标准版", value: 1 },
  { label: "PX授权版", value: 2 },
  { label: "PXT", value: 3 },
];
const DEBUG_DEVICE_OPTIONS = [
  { label: "不需要", value: 0 },
  { label: "需要", value: 1 },
];
const STATUS = {
  FORM: "form",
  PENDING: "pending",
  REJECTED: "rejected",
  SUCCESS: "success",
};
const AUDIT_STATUS_MAP = {
  1: STATUS.PENDING,
  2: STATUS.SUCCESS,
  3: STATUS.REJECTED,
};

/**
 * License 账号申请
 */
export default {
  name: "LicenseAccountApply",
  components: {
    FormFileUpload,
  },
  props: {
    company: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      STATUS,
      status: STATUS.FORM,
      rejectReason: "",
      submitting: false,
      loading: false,
      successAttachment: "",
      zipSizeText: "",
      zipName: "",
      regionOptions: [],
      approverOptions: [],
      approverSearchLoading: false,
      productVersionOptions: PRODUCT_VERSION_OPTIONS,
      debugDeviceOptions: DEBUG_DEVICE_OPTIONS,
      mapProvinceData: [],
      mapDataLoading: false,
      areaCascaderProps: {
        expandTrigger: "hover",
        lazy: true,
      },
      /** 项目地址级联选中值（路径唯一 key，非 adcode） */
      projectAddressCodes: [],
      form: {
        id: "",
        region: "",
        approver: "",
        approverId: "",
        projectLeader: "",
        leaderPhone: "",
        factoryName: "",
        productName: "",
        productVersion: 1,
        plannedDeployTime: "",
        projectName: "",
        projectAddress: "",
        projectAddressAdcodes: [],
        macAddress: "",
        detailAddress: "",
        debugDevice: 0,
        attachment: "",
      },
      rules: {
        region: [
          { required: true, message: "请选择所在大区", trigger: "change" },
        ],
        approver: [
          { required: true, message: "请选择审批人", trigger: "change" },
        ],
        projectLeader: [
          { required: true, message: "请输入项目负责人", trigger: "blur" },
        ],
        leaderPhone: [
          { required: true, message: "请输入负责人联系方式", trigger: "blur" },
          {
            pattern: /^1[3-9]\d{9}$/,
            message: "手机号格式不正确",
            trigger: "blur",
          },
        ],
        factoryName: [
          { required: true, message: "请输入盘厂名称", trigger: "blur" },
        ],
        productName: [
          { required: true, message: "请输入产品名称", trigger: "blur" },
        ],
        productVersion: [
          { required: true, message: "请选择产品版本", trigger: "change" },
        ],
        plannedDeployTime: [
          { required: true, message: "请选择计划部署时间", trigger: "change" },
        ],
        projectName: [
          { required: true, message: "请输入项目名称", trigger: "blur" },
        ],
        projectAddress: [
          {
            required: true,
            message: "请选择项目地址（至少到区/县）",
            trigger: "change",
          },
          {
            validator: (rule, value, callback) => {
              if (
                !this.projectAddressCodes ||
                this.projectAddressCodes.length < 3
              ) {
                callback(new Error("请选择项目地址（至少到区/县）"));
                return;
              }
              callback();
            },
            trigger: "change",
          },
        ],
        macAddress: [
          { required: true, message: "请输入MAC地址", trigger: "blur" },
          {
            pattern: /^([0-9A-Fa-f]{2}([-:])){5}[0-9A-Fa-f]{2}$/,
            message: "MAC地址格式不正确",
            trigger: "blur",
          },
        ],
        detailAddress: [
          { required: true, message: "请输入详细地址", trigger: "blur" },
        ],
        debugDevice: [
          { required: true, message: "请选择调试设备", trigger: "change" },
        ],
      },
    };
  },
  computed: {
    isViewMode() {
      return this.$route.query.mode === "view";
    },
    isEditMode() {
      return !!(this.$route.query.id || this.form.id) && !this.isViewMode;
    },
    formDisabled() {
      return (
        this.status === STATUS.PENDING ||
        this.status === STATUS.SUCCESS ||
        this.isViewMode
      );
    },
  },
  created() {
    this.areaCascaderProps.lazyLoad = this.loadAreaCascaderNode;
    this.loadMapData();
    this.fetchRegionList();
    this.searchApproverMethod("");
    this.fetchDetail();
  },

  methods: {
    /** 加载在线省市区地址数据 */
    loadMapData() {
      this.mapDataLoading = true;
      fetchMapData()
        .then((data) => {
          this.mapProvinceData = data.provinceData || [];
        })
        .catch(() => {
          this.$message.error("地址数据加载失败，请稍后重试");
        })
        .finally(() => {
          this.mapDataLoading = false;
        });
    },
    /**
     * 生成级联唯一 value（街道级 adcode 与区县重复，不能用 adcode）
     * @param {string} parentPath
     * @param {Object} item
     */
    buildAreaNodeValue(parentPath, item) {
      return parentPath ? `${parentPath}/${item.name}` : item.name;
    },
    /** 懒加载省市区街道（按需展开，避免全量建树卡顿） */
    loadAreaCascaderNode(node, resolve) {
      console.log(
        "【License地图加载】进入 lazyLoad, level:",
        node.level,
        "value:",
        node.value
      );
      const render = () => {
        let list = [];
        if (node.root || node.level === 0) {
          list = this.mapProvinceData;
        } else if (node.data && node.data.raw) {
          list = node.data.raw.districts || node.data.raw.children || [];
        }
        const parentPath =
          node.root || node.level === 0
            ? ""
            : (node.data && node.data.value) || "";
        const options = list.map((item) => {
          const value = this.buildAreaNodeValue(parentPath, item);
          const hasChildren =
            (item.districts && item.districts.length > 0) ||
            (item.children && item.children.length > 0);
          return {
            value,
            label: item.name,
            leaf: !hasChildren,
            raw: item,
          };
        });
        console.log(
          "【License地图加载】完成 resolve, level:",
          node.level,
          "options数量:",
          options.length,
          "第一项的值:",
          options[0] ? options[0].value : "none"
        );
        resolve(options);
      };

      if (this.mapProvinceData.length) {
        render();
        return;
      }

      fetchMapData()
        .then((data) => {
          this.mapProvinceData = data.provinceData || [];
          render();
        })
        .catch(() => resolve([]));
    },
    /** 项目地址级联变更：同步文案与 adcode 路径 */
    handleProjectAddressChange() {
      const cascader = this.$refs.projectAddressCascader;
      const nodes = cascader && cascader.getCheckedNodes(true);
      const node = nodes && nodes[0];
      if (node) {
        const pathNodes = node.pathNodes || [node];
        const labels = pathNodes.map(
          (n) => n.label || (n.data && n.data.label)
        );
        this.form.projectAddress = labels.filter(Boolean).join("/");
        this.form.projectAddressAdcodes = pathNodes.map(
          (n) =>
            (n.data && n.data.raw && (n.data.raw.adcode || n.data.raw.id)) || ""
        );
      } else {
        this.form.projectAddress = "";
        this.form.projectAddressAdcodes = [];
      }
      this.$refs.licenseForm &&
        this.$refs.licenseForm.validateField("projectAddress");
    },

    handleAttachmentUpload(url) {
      this.form.attachment = url || "";
    },
    fetchRegionList() {
      getAuditorRegionList().then((res) => {
        if (res.code === 200) {
          const list = res.data || [];
          this.regionOptions = list.map((item) => {
            const label = item.name || item.regionName || item.label || item;
            const value = item.name || item.regionName || item.value || item;
            return { label, value };
          });
        }
      });
    },
    searchApproverMethod(query) {
      this.approverSearchLoading = true;
      searchAuditor({ name: query || "" })
        .then((res) => {
          if (res.code === 200) {
            const list = res.data || [];
            this.approverOptions = list.map((item) => {
              return {
                label: item.name,
                value: item.name,
                id: item.id,
                region: item.region,
              };
            });
          } else {
            this.approverOptions = [];
          }
        })
        .catch(() => {
          this.approverOptions = [];
        })
        .finally(() => {
          this.approverSearchLoading = false;
        });
    },
    handleApproverChange(val) {
      const option = this.approverOptions.find((o) => o.value === val);
      if (option) {
        this.form.approverId = option.id;
        if (option.region) {
          this.form.region = option.region;
        }
      } else {
        this.form.approverId = "";
      }
    },
    resolveStatus(raw) {
      if (raw === undefined || raw === null || raw === "") {
        return STATUS.FORM;
      }
      const key = typeof raw === "string" ? raw.toLowerCase() : raw;
      return AUDIT_STATUS_MAP[key] || STATUS.FORM;
    },
    /**
     * 根据中文路径组装对应的 adcodes
     * @param {string} address - 例如 "安徽省/合肥市/蜀山区/荷叶地街道"
     * @returns {string[]} adcodes 数组
     */
    findAdcodesByAddress(address) {
      if (
        !address ||
        !this.mapProvinceData ||
        this.mapProvinceData.length === 0
      )
        return [];
      const parts = address.split("/");
      const adcodes = [];
      let currentList = this.mapProvinceData;

      for (let i = 0; i < parts.length; i++) {
        const name = parts[i];
        const match = currentList.find((item) => item.name === name);
        if (match) {
          adcodes.push(match.adcode || match.id || "");
          currentList = match.districts || match.children || [];
        } else {
          break;
        }
      }
      return adcodes;
    },
    fetchDetail() {
      this.loading = true;
      getLicenseApplyDetail()
        .then((res) => {
          if (res.code === 200) {
            const data = res.data;
            if (!data) return;

            // 解析状态并赋给本地状态
            const auditStatus =
              data.auditStatus !== undefined ? data.auditStatus : data.status;
            this.status = this.resolveStatus(auditStatus);
            this.rejectReason = data.rejectReason || data.auditReason || "";

            // 获取附件下载信息
            this.successAttachment = data.successAttachment || "";
            this.zipSizeText = data.fileSizeText || data.zipSizeText || "";
            if (this.successAttachment) {
              this.zipName = this.normalizeZipName(this.successAttachment);
            }

            this.form = {
              id: data.id || "",
              region: data.region || "",
              approver: data.approver || "",
              approverId: data.approverId || "",
              projectLeader: data.projectLeader || "",
              leaderPhone: data.leaderPhone || "",
              factoryName: data.factoryName || "",
              productName: data.productName || "",
              productVersion:
                data.productVersion !== undefined &&
                data.productVersion !== null &&
                data.productVersion !== ""
                  ? Number(data.productVersion)
                  : 1,
              plannedDeployTime: data.plannedDeployTime || "",
              projectName: data.projectName || "",
              projectAddress: data.projectAddress || "",
              projectAddressAdcodes: data.projectAddressAdcodes || [],
              macAddress: data.macAddress || "",
              detailAddress: data.detailAddress || "",
              debugDevice:
                data.debugDevice !== undefined &&
                data.debugDevice !== null &&
                data.debugDevice !== ""
                  ? Number(data.debugDevice)
                  : 0,
              attachment: data.attachment || "",
            };

            // 兜底动态加入大区和审批人选项以确保回显显示正确名称
            if (data.region) {
              const exists = this.regionOptions.some(
                (o) => o.value === data.region
              );
              if (!exists) {
                this.regionOptions.push({
                  label: data.region,
                  value: data.region,
                });
              }
            }
            if (data.approver) {
              const exists = this.approverOptions.some(
                (o) => o.value === data.approver
              );
              if (!exists) {
                this.approverOptions.push({
                  label: data.approver,
                  value: data.approver,
                  id: data.approverId || "",
                });
              }
            }

            // 解析级联省市区街道 projectAddressCodes 并组装 projectAddressAdcodes
            const processMapDataAndAssemble = () => {
              if (this.form.projectAddress) {
                const parts = this.form.projectAddress.split("/");
                const codes = [];
                let current = "";
                for (let i = 0; i < parts.length; i++) {
                  current = current ? `${current}/${parts[i]}` : parts[i];
                  codes.push(current);
                }
                console.log(
                  "【License回显】欲赋值的 codes 数组:",
                  JSON.stringify(codes)
                );
                this.projectAddressCodes = codes;
                console.log(
                  "【License回显】已赋值 projectAddressCodes:",
                  JSON.stringify(this.projectAddressCodes)
                );

                this.form.projectAddressAdcodes = this.findAdcodesByAddress(
                  this.form.projectAddress
                );
                console.log(
                  "【License回显】已组装 projectAddressAdcodes:",
                  JSON.stringify(this.form.projectAddressAdcodes)
                );
              }
            };

            if (this.mapProvinceData && this.mapProvinceData.length > 0) {
              processMapDataAndAssemble();
            } else {
              fetchMapData()
                .then((mapData) => {
                  this.mapProvinceData = mapData.provinceData || [];
                  processMapDataAndAssemble();
                })
                .catch((err) => {
                  console.error("【License回显】加载地图数据失败:", err);
                });
            }
          } else {
            this.$message.error(res.msg || "获取申请详情失败");
          }
        })
        .catch((err) => {
          console.error(err);
          this.$message.error("获取详情服务异常");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleSubmit() {
      this.$refs.licenseForm.validate((valid) => {
        if (!valid) return;
        this.submitting = true;
        const submitData = {
          ...this.form,
        };
        const apiCall = this.isEditMode
          ? updateLicenseApply(submitData)
          : addLicenseApply(submitData);

        apiCall
          .then((res) => {
            if (res.code === 200) {
              this.$message.success(
                this.isEditMode ? "保存成功" : "申请已提交"
              );
              if (res.data) {
                this.form.id = res.data;
              }
              this.$emit("submit", submitData);
              this.fetchDetail();
            } else {
              this.$message.error(res.msg || "操作失败");
            }
          })
          .catch((err) => {
            console.error(err);
            this.$message.error("申请提交失败，请稍后重试");
          })
          .finally(() => {
            this.submitting = false;
          });
      });
    },
    normalizeZipName(url) {
      if (!url) return "License文件资料包.zip";
      let cleanName = String(url).split("?")[0].split("/").pop();
      if (!cleanName) return "License文件资料包.zip";
      try {
        cleanName = decodeURIComponent(cleanName);
      } catch (e) {}
      return /\.zip$/i.test(cleanName) ? cleanName : `${cleanName}`;
    },
    handleDownloadZip() {
      if (!this.successAttachment) {
        this.$message.warning("未获取到下载链接");
        return;
      }
      window.open(this.successAttachment, "_blank");
    },
  },
};
</script>

<style lang="less" scoped>
.statusBanner {
  margin-bottom: 24px;
  padding: 10px 24px;
  font-size: 14px;
  line-height: 22px;
  text-align: center;
  border-radius: 8px;
}
.statusBanner--pending {
  background: #fff7e6;
  color: #d46b08;
}
.statusBanner--rejected {
  background: #fff1f0;
  color: #cf1322;
}
.statusBanner--success {
  background: #e6fffb;
  color: #08979c;
}
.licenseAccountApply {
  width: 100%;
  padding: 0 0 10px;
}
/deep/ .el-form-item.is-disabled .el-input__inner,
/deep/ .el-form-item.is-disabled .el-input.is-disabled .el-input__inner {
  background-color: #f5f7fa;
  color: #606266;
}
.licenseAccountApplyTitle {
  margin: 0 0 40px;
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
  text-align: center;
  line-height: 1.4;
}
.licenseAccountApplyForm {
  max-width: 960px;
  margin: 0 auto;
  /deep/ .el-input,
  /deep/ .el-select,
  /deep/ .el-cascader,
  /deep/ .el-date-editor {
    width: 100%;
  }
}
.licenseAccountApplyFields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 48px;
}
.licenseAccountApplyFull {
  width: 100%;
}
.licenseAccountApplyDebugItem {
  /deep/ .el-form-item__content {
    line-height: normal;
  }
}
.licenseAccountApplyHint {
  margin: 4px 0 0;
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.4;
}
.licenseAccountApplyFooter {
  margin-top: 48px;
  display: flex;
  justify-content: center;
}
.licenseAccountApplySubmitBtn {
  min-width: 200px;
  height: 40px;
  padding: 0 80px;
  background: #00d8c3;
  border-color: #00d8c3;
  font-size: 15px;
  &:hover,
  &:focus {
    background: #00c2b0;
    border-color: #00c2b0;
  }
}
/deep/ .el-form-item__label {
  font-size: 14px;
  color: #525f66;
  line-height: 40px;
}
/deep/ .el-form-item__label::before {
  color: #f5222d;
}
/deep/ .el-input__inner,
/deep/ .el-select .el-input__inner,
/deep/ .el-date-editor.el-input {
  height: 40px;
  line-height: 40px;
  border-radius: 8px;
}
/deep/ .el-input__prefix {
  display: flex;
  align-items: center;
  left: 10px;
  color: #9ca3af;
}
/deep/ .el-input--prefix .el-input__inner {
  padding-left: 36px;
}
.licenseAccountApplyCascader {
  /deep/ .el-input__inner {
    padding-left: 15px;
  }
}
.licenseSuccessContainer {
  max-width: 600px;
  margin: 40px auto 0;
}
.licenseFileSection {
  margin-bottom: 24px;
}
.licenseFileTitle {
  font-size: 14px;
  color: #9ca3af;
  margin-bottom: 12px;
  font-weight: normal;
}
.licenseFileCard {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  transition: all 0.3s ease;
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
    border-color: #cbd5e1;
  }
}
.licenseFileIconWrap {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: #eff6ff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  i {
    font-size: 24px;
    color: #2563eb;
  }
}
.licenseFileInfo {
  flex: 1;
  min-width: 0;
}
.licenseFileName {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.licenseFileSize {
  font-size: 12px;
  color: #64748b;
}
.licenseDownloadBtn {
  width: 36px;
  height: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-color: #cbd5e1;
  background: #fff;
  color: #475569;
  font-size: 16px;
  &:hover {
    background: #eff6ff;
    color: #2563eb;
    border-color: #bfdbfe;
  }
}
.licenseTipCard {
  display: flex;
  align-items: flex-start;
  padding: 14px 18px;
  background: #fffbeb;
  border: 1px solid #fef3c7;
  border-radius: 8px;
  color: #d97706;
  font-size: 13px;
  line-height: 1.6;
}
.licenseTipIcon {
  font-size: 16px;
  margin-right: 8px;
  margin-top: 2px;
}
.licenseTipText {
  flex: 1;
}
.licenseAccountApplyCascader.has-placeholder-value
  /deep/
  .el-input__inner::placeholder {
  color: #606266;
}
</style>
